import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  AutoComplete,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { projectTemplates, projectTemplateChildren } from "../../lib/api";
import { DEFAULT_TEMPLATE_ROLE_SUGGESTIONS } from "../../lib/templateRoles";
import type {
  ProjectTemplate,
  TaskStatus,
  TemplateMetric,
  TemplateStage,
  TemplateSubtask,
  TemplateTask,
} from "../../lib/types";

interface Props {
  template: ProjectTemplate;
  open: boolean;
  onClose: () => void;
}

const STATUS_OPTIONS: { label: string; value: TaskStatus }[] = [
  { label: "To do", value: "todo" },
  { label: "In progress", value: "in_progress" },
  { label: "Blocked", value: "blocked" },
  { label: "Done", value: "done" },
];

function byOrder<T extends { order?: number; createdAt?: string | null }>(a: T, b: T) {
  const ao = a.order ?? 0;
  const bo = b.order ?? 0;
  if (ao !== bo) return ao - bo;
  return (a.createdAt || "").localeCompare(b.createdAt || "");
}

/**
 * OAIP-44 — full editor for a single project plan TEMPLATE. Mirrors the live
 * Project Plan tab, but with relative day-offset inputs instead of date
 * pickers, free-text role placeholders instead of a user picker, and a
 * task-multi-select for structured dependencies. Every write goes through the
 * audited `projectTemplateChildren` / `projectTemplates` Admin-SDK handlers.
 */
export default function ProjectTemplateEditor({ template, open, onClose }: Props) {
  const { message, modal } = App.useApp();

  const [stages, setStages] = useState<TemplateStage[]>([]);
  const [tasks, setTasks] = useState<TemplateTask[]>([]);
  const [subtasks, setSubtasks] = useState<TemplateSubtask[]>([]);
  const [metrics, setMetrics] = useState<TemplateMetric[]>([]);

  const [metaForm] = Form.useForm();
  const [savingMeta, setSavingMeta] = useState(false);
  const [newStageName, setNewStageName] = useState("");

  const [taskModal, setTaskModal] = useState<{ open: boolean; editing: TemplateTask | null; stageId: string }>(
    { open: false, editing: null, stageId: "" },
  );
  const [subtaskModal, setSubtaskModal] = useState<{ open: boolean; editing: TemplateSubtask | null; taskId: string }>(
    { open: false, editing: null, taskId: "" },
  );
  const [metricModal, setMetricModal] = useState<{ open: boolean; editing: TemplateMetric | null }>(
    { open: false, editing: null },
  );

  const templateId = template.id;

  // Subscribe to children. We filter by templateId (single-field, auto-indexed)
  // and sort client-side so no composite index is required for the editor.
  useEffect(() => {
    if (!open) return;
    const subs = [
      onSnapshot(query(collection(db, "projectTemplateStages"), where("templateId", "==", templateId)), (snap) =>
        setStages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TemplateStage, "id">) })).sort(byOrder)),
      ),
      onSnapshot(query(collection(db, "projectTemplateTasks"), where("templateId", "==", templateId)), (snap) =>
        setTasks(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TemplateTask, "id">) })).sort(byOrder)),
      ),
      onSnapshot(query(collection(db, "projectTemplateSubtasks"), where("templateId", "==", templateId)), (snap) =>
        setSubtasks(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TemplateSubtask, "id">) })).sort(byOrder)),
      ),
      onSnapshot(query(collection(db, "projectTemplateMetrics"), where("templateId", "==", templateId)), (snap) =>
        setMetrics(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TemplateMetric, "id">) })).sort(byOrder)),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [open, templateId]);

  useEffect(() => {
    if (open) {
      metaForm.setFieldsValue({ name: template.name, description: template.description || "" });
    }
  }, [open, template, metaForm]);

  const taskOptions = useMemo(
    () => tasks.map((t) => ({ value: t.id, label: t.title || "(untitled task)" })),
    [tasks],
  );
  const roleOptions = useMemo(
    () => DEFAULT_TEMPLATE_ROLE_SUGGESTIONS.map((r) => ({ value: r })),
    [],
  );

  const handleSaveMeta = async () => {
    try {
      const values = await metaForm.validateFields();
      setSavingMeta(true);
      await projectTemplates.update(templateId, {
        name: values.name,
        description: values.description || "",
      });
      message.success("Template details saved.");
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSavingMeta(false);
    }
  };

  // --- Stages --------------------------------------------------------------
  const handleAddStage = async () => {
    const name = newStageName.trim();
    if (!name) return;
    const order = stages.length ? Math.max(...stages.map((s) => s.order ?? 0)) + 1 : 0;
    try {
      await projectTemplateChildren.add(templateId, "stages", { name, order });
      setNewStageName("");
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };

  const handleRenameStage = async (stage: TemplateStage, name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === stage.name) return;
    try {
      await projectTemplateChildren.update(templateId, "stages", stage.id, { name: trimmed });
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };

  const swapStageOrder = async (a: TemplateStage, b: TemplateStage) => {
    try {
      await Promise.all([
        projectTemplateChildren.update(templateId, "stages", a.id, { order: b.order ?? 0 }),
        projectTemplateChildren.update(templateId, "stages", b.id, { order: a.order ?? 0 }),
      ]);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };

  const handleDeleteStage = (stage: TemplateStage) => {
    const stageTasks = tasks.filter((t) => t.stageId === stage.id);
    if (stageTasks.length > 0) {
      message.warning("Delete or move the stage's tasks first.");
      return;
    }
    modal.confirm({
      title: `Delete stage "${stage.name}"?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => projectTemplateChildren.delete(templateId, "stages", stage.id),
    });
  };

  // --- Tasks / Subtasks / Metrics deletes ----------------------------------
  const handleDeleteTask = (task: TemplateTask) => {
    modal.confirm({
      title: `Delete task "${task.title}" and its subtasks?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await Promise.all(
          subtasks
            .filter((s) => s.taskId === task.id)
            .map((s) => projectTemplateChildren.delete(templateId, "subtasks", s.id)),
        );
        await projectTemplateChildren.delete(templateId, "tasks", task.id);
      },
    });
  };

  const handleDeleteSubtask = (sub: TemplateSubtask) =>
    modal.confirm({
      title: `Delete subtask "${sub.title}"?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => projectTemplateChildren.delete(templateId, "subtasks", sub.id),
    });

  const handleDeleteMetric = (metric: TemplateMetric) =>
    modal.confirm({
      title: `Delete metric "${metric.title}"?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => projectTemplateChildren.delete(templateId, "metrics", metric.id),
    });

  const taskTitleById = useMemo(() => {
    const m = new Map<string, string>();
    tasks.forEach((t) => m.set(t.id, t.title || "(untitled task)"));
    return m;
  }, [tasks]);

  const renderTask = (task: TemplateTask) => {
    const taskSubs = subtasks.filter((s) => s.taskId === task.id).sort(byOrder);
    const deps = (task.dependsOnTaskIds || []).map((id) => taskTitleById.get(id)).filter(Boolean);
    return (
      <List.Item
        key={task.id}
        actions={[
          <Tooltip key="sub" title="Add subtask">
            <Button
              type="text"
              icon={<PlusOutlined />}
              aria-label={`Add subtask to ${task.title}`}
              onClick={() => setSubtaskModal({ open: true, editing: null, taskId: task.id })}
            >
              Subtask
            </Button>
          </Tooltip>,
          <Tooltip key="edit" title="Edit task">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label={`Edit task ${task.title}`}
              onClick={() => setTaskModal({ open: true, editing: task, stageId: task.stageId || "" })}
            />
          </Tooltip>,
          <Tooltip key="del" title="Delete task">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete task ${task.title}`}
              onClick={() => handleDeleteTask(task)}
            />
          </Tooltip>,
        ]}
      >
        <List.Item.Meta
          title={
            <Space wrap>
              <Typography.Text strong>{task.title || "(untitled task)"}</Typography.Text>
              <Tag>
                day {task.startOffsetDays ?? 0} → {task.endOffsetDays ?? "?"}
              </Tag>
              {deps.length > 0 && <Tag color="blue">depends: {deps.join(", ")}</Tag>}
            </Space>
          }
          description={
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              {task.description && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {task.description}
                </Typography.Text>
              )}
              {taskSubs.length > 0 && (
                <List
                  size="small"
                  dataSource={taskSubs}
                  renderItem={(sub) => (
                    <List.Item
                      key={sub.id}
                      actions={[
                        <Button
                          key="edit"
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          aria-label={`Edit subtask ${sub.title}`}
                          onClick={() => setSubtaskModal({ open: true, editing: sub, taskId: task.id })}
                        />,
                        <Button
                          key="del"
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={`Delete subtask ${sub.title}`}
                          onClick={() => handleDeleteSubtask(sub)}
                        />,
                      ]}
                    >
                      <Space wrap size={4}>
                        <Typography.Text>{sub.title}</Typography.Text>
                        {sub.assigneeRole && <Tag color="purple">{sub.assigneeRole}</Tag>}
                        <Tag>{sub.defaultStatus || "todo"}</Tag>
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };

  return (
    <Modal
      title={`Edit project template`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="done" type="primary" onClick={onClose}>
          Done
        </Button>,
      ]}
      width={900}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message={`Editing TEMPLATE: ${template.name}`}
          description="This is a reusable plan applied to FUTURE projects at convert time. Editing it does not change any existing project."
        />

        <Card size="small" title="Template details">
          <Form form={metaForm} layout="vertical">
            <Form.Item label="Name" name="name" rules={[{ required: true, message: "Enter a template name" }]}>
              <Input placeholder="e.g. OAIP Standard AI Project" />
            </Form.Item>
            <Form.Item label="Description" name="description">
              <Input.TextArea autoSize={{ minRows: 2 }} placeholder="When should this template be used?" />
            </Form.Item>
            <Button type="primary" loading={savingMeta} onClick={handleSaveMeta}>
              Save details
            </Button>
          </Form>
        </Card>

        <Card
          size="small"
          title="Stages & tasks"
          extra={
            <Space.Compact>
              <Input
                placeholder="New stage name"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onPressEnter={handleAddStage}
                aria-label="New stage name"
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddStage}>
                Add stage
              </Button>
            </Space.Compact>
          }
        >
          {stages.length === 0 ? (
            <Empty description="No stages yet. Add a stage to start building the plan." />
          ) : (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {stages.map((stage, idx) => {
                const stageTasks = tasks.filter((t) => t.stageId === stage.id).sort(byOrder);
                return (
                  <Card
                    key={stage.id}
                    type="inner"
                    size="small"
                    title={
                      <Space>
                        <Typography.Text
                          strong
                          editable={{ onChange: (val) => handleRenameStage(stage, val) }}
                        >
                          {stage.name}
                        </Typography.Text>
                      </Space>
                    }
                    extra={
                      <Space>
                        <Tooltip title="Move up">
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowUpOutlined />}
                            disabled={idx === 0}
                            aria-label={`Move stage ${stage.name} up`}
                            onClick={() => swapStageOrder(stage, stages[idx - 1])}
                          />
                        </Tooltip>
                        <Tooltip title="Move down">
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowDownOutlined />}
                            disabled={idx === stages.length - 1}
                            aria-label={`Move stage ${stage.name} down`}
                            onClick={() => swapStageOrder(stage, stages[idx + 1])}
                          />
                        </Tooltip>
                        <Button
                          type="primary"
                          ghost
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => setTaskModal({ open: true, editing: null, stageId: stage.id })}
                        >
                          Task
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={`Delete stage ${stage.name}`}
                          onClick={() => handleDeleteStage(stage)}
                        />
                      </Space>
                    }
                  >
                    {stageTasks.length === 0 ? (
                      <Typography.Text type="secondary">No tasks in this stage yet.</Typography.Text>
                    ) : (
                      <List dataSource={stageTasks} renderItem={renderTask} />
                    )}
                  </Card>
                );
              })}
            </Space>
          )}
        </Card>

        <Card
          size="small"
          title="Success metrics"
          extra={
            <Button
              type="primary"
              ghost
              icon={<PlusOutlined />}
              onClick={() => setMetricModal({ open: true, editing: null })}
            >
              Add metric
            </Button>
          }
        >
          {metrics.length === 0 ? (
            <Empty description="No metrics yet." />
          ) : (
            <List
              dataSource={metrics.slice().sort(byOrder)}
              renderItem={(metric) => {
                const deps = (metric.dependsOnTaskIds || []).map((id) => taskTitleById.get(id)).filter(Boolean);
                return (
                  <List.Item
                    key={metric.id}
                    actions={[
                      <Button
                        key="edit"
                        type="text"
                        icon={<EditOutlined />}
                        aria-label={`Edit metric ${metric.title}`}
                        onClick={() => setMetricModal({ open: true, editing: metric })}
                      />,
                      <Button
                        key="del"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={`Delete metric ${metric.title}`}
                        onClick={() => handleDeleteMetric(metric)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space wrap>
                          <Typography.Text strong>{metric.title}</Typography.Text>
                          {metric.dueOffsetDays != null && <Tag>due day {metric.dueOffsetDays}</Tag>}
                          {deps.length > 0 && <Tag color="blue">depends: {deps.join(", ")}</Tag>}
                        </Space>
                      }
                      description={metric.description || undefined}
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Card>
      </Space>

      <TaskFormModal
        state={taskModal}
        templateId={templateId}
        stages={stages}
        tasks={tasks}
        taskOptions={taskOptions}
        onClose={() => setTaskModal({ open: false, editing: null, stageId: "" })}
      />
      <SubtaskFormModal
        state={subtaskModal}
        templateId={templateId}
        subtasks={subtasks}
        roleOptions={roleOptions}
        onClose={() => setSubtaskModal({ open: false, editing: null, taskId: "" })}
      />
      <MetricFormModal
        state={metricModal}
        templateId={templateId}
        metrics={metrics}
        taskOptions={taskOptions}
        onClose={() => setMetricModal({ open: false, editing: null })}
      />
    </Modal>
  );
}

// --- Task form modal -------------------------------------------------------
function TaskFormModal({
  state,
  templateId,
  stages,
  tasks,
  taskOptions,
  onClose,
}: {
  state: { open: boolean; editing: TemplateTask | null; stageId: string };
  templateId: string;
  stages: TemplateStage[];
  tasks: TemplateTask[];
  taskOptions: { value: string; label: string }[];
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    const t = state.editing;
    form.setFieldsValue({
      title: t?.title || "",
      stageId: t?.stageId || state.stageId || undefined,
      description: t?.description || "",
      startOffsetDays: t?.startOffsetDays ?? 0,
      endOffsetDays: t?.endOffsetDays ?? null,
      dependsOnTaskIds: t?.dependsOnTaskIds || [],
    });
  }, [state, form]);

  const depOptions = taskOptions.filter((o) => o.value !== state.editing?.id);

  const handleOk = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const data: Record<string, unknown> = {
        title: v.title,
        stageId: v.stageId,
        description: v.description || "",
        startOffsetDays: v.startOffsetDays ?? null,
        endOffsetDays: v.endOffsetDays ?? null,
        dependsOnTaskIds: v.dependsOnTaskIds || [],
      };
      if (state.editing) {
        await projectTemplateChildren.update(templateId, "tasks", state.editing.id, data);
      } else {
        const siblings = tasks.filter((t) => t.stageId === v.stageId);
        data.order = siblings.length ? Math.max(...siblings.map((t) => t.order ?? 0)) + 1 : 0;
        await projectTemplateChildren.add(templateId, "tasks", data);
      }
      onClose();
    } catch (err) {
      if (err instanceof Error && err.message) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={state.editing ? "Edit template task" : "New template task"}
      open={state.open}
      onCancel={onClose}
      onOk={handleOk}
      okButtonProps={{ loading: saving }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Task title" name="title" rules={[{ required: true, message: "Enter a title" }]}>
          <Input placeholder="Task title" />
        </Form.Item>
        <Form.Item label="Stage" name="stageId" rules={[{ required: true, message: "Pick a stage" }]}>
          <Select options={stages.map((s) => ({ value: s.id, label: s.name }))} placeholder="Pick a stage" />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea autoSize={{ minRows: 2 }} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Starts day +N (from project start)"
              name="startOffsetDays"
              tooltip="Relative offset in days from the project's start date."
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Ends day +N" name="endOffsetDays">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Depends on tasks" name="dependsOnTaskIds">
          <Select mode="multiple" allowClear options={depOptions} placeholder="Select prerequisite tasks" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// --- Subtask form modal ----------------------------------------------------
function SubtaskFormModal({
  state,
  templateId,
  subtasks,
  roleOptions,
  onClose,
}: {
  state: { open: boolean; editing: TemplateSubtask | null; taskId: string };
  templateId: string;
  subtasks: TemplateSubtask[];
  roleOptions: { value: string }[];
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    const s = state.editing;
    form.setFieldsValue({
      title: s?.title || "",
      assigneeRole: s?.assigneeRole || "",
      defaultStatus: s?.defaultStatus || "todo",
    });
  }, [state, form]);

  const handleOk = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const data: Record<string, unknown> = {
        title: v.title,
        assigneeRole: v.assigneeRole?.trim() || null,
        defaultStatus: v.defaultStatus || "todo",
      };
      if (state.editing) {
        await projectTemplateChildren.update(templateId, "subtasks", state.editing.id, data);
      } else {
        data.taskId = state.taskId;
        const siblings = subtasks.filter((s) => s.taskId === state.taskId);
        data.order = siblings.length ? Math.max(...siblings.map((s) => s.order ?? 0)) + 1 : 0;
        await projectTemplateChildren.add(templateId, "subtasks", data);
      }
      onClose();
    } catch (err) {
      if (err instanceof Error && err.message) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={state.editing ? "Edit template subtask" : "New template subtask"}
      open={state.open}
      onCancel={onClose}
      onOk={handleOk}
      okButtonProps={{ loading: saving }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Subtask title" name="title" rules={[{ required: true, message: "Enter a title" }]}>
          <Input placeholder="Subtask title" />
        </Form.Item>
        <Form.Item
          label="Assignee role"
          name="assigneeRole"
          tooltip="A role placeholder (free text). Mapped to a real user when the template is applied."
        >
          <AutoComplete options={roleOptions} placeholder="e.g. Project Lead" allowClear />
        </Form.Item>
        <Form.Item label="Default status" name="defaultStatus">
          <Select options={STATUS_OPTIONS} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// --- Metric form modal -----------------------------------------------------
function MetricFormModal({
  state,
  templateId,
  metrics,
  taskOptions,
  onClose,
}: {
  state: { open: boolean; editing: TemplateMetric | null };
  templateId: string;
  metrics: TemplateMetric[];
  taskOptions: { value: string; label: string }[];
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    const m = state.editing;
    form.setFieldsValue({
      title: m?.title || "",
      description: m?.description || "",
      dueOffsetDays: m?.dueOffsetDays ?? null,
      dependsOnTaskIds: m?.dependsOnTaskIds || [],
    });
  }, [state, form]);

  const handleOk = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const data: Record<string, unknown> = {
        title: v.title,
        description: v.description || "",
        dueOffsetDays: v.dueOffsetDays ?? null,
        dependsOnTaskIds: v.dependsOnTaskIds || [],
      };
      if (state.editing) {
        await projectTemplateChildren.update(templateId, "metrics", state.editing.id, data);
      } else {
        data.order = metrics.length ? Math.max(...metrics.map((m) => m.order ?? 0)) + 1 : 0;
        await projectTemplateChildren.add(templateId, "metrics", data);
      }
      onClose();
    } catch (err) {
      if (err instanceof Error && err.message) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={state.editing ? "Edit template metric" : "New template metric"}
      open={state.open}
      onCancel={onClose}
      onOk={handleOk}
      okButtonProps={{ loading: saving }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Metric title" name="title" rules={[{ required: true, message: "Enter a title" }]}>
          <Input placeholder="e.g. Pilot accuracy ≥ 90%" />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea autoSize={{ minRows: 2 }} />
        </Form.Item>
        <Form.Item label="Due day +N (from project start)" name="dueOffsetDays">
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Depends on tasks" name="dependsOnTaskIds">
          <Select mode="multiple" allowClear options={taskOptions} placeholder="Select prerequisite tasks" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
